import { useState, useEffect } from 'react';

import './CounterInput.css';

interface CounterInputProps {
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

const CounterInput = ({
  value = 0,
  onChange,
  min = 1,
  max = Infinity,
  step = 1,
  disabled = false,
  className = '',
  placeholder = '0'
}: CounterInputProps) => {
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleIncrement = () => {
    if (disabled) return;

    const newValue = Math.min(max, internalValue + step);
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  const handleDecrement = () => {
    if (disabled) return;

    const newValue = Math.max(min, internalValue - step);
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Allow empty string for better UX while typing
    if (inputValue === '') {
      setInternalValue(1);
      onChange?.(1);
      return;
    }

    const numValue = parseInt(inputValue) || 1;
    const clampedValue = Math.max(min, Math.min(max, numValue));

    setInternalValue(clampedValue);
    onChange?.(clampedValue);
  };

  const handleInputBlur = () => {
    // Ensure value is within bounds when input loses focus
    const clampedValue = Math.max(min, Math.min(max, internalValue));
    if (clampedValue !== internalValue) {
      setInternalValue(clampedValue);
      onChange?.(clampedValue);
    }
  };

  const isMinusDisabled = disabled || internalValue <= min;
  const isPlusDisabled = disabled || internalValue >= max;

  return (
    <div className={`counter-input ${className} ${disabled ? 'disabled' : ''}`}>
      <button
        type="button"
        className="counter-btn counter-btn-minus"
        onClick={handleDecrement}
        disabled={isMinusDisabled}
        aria-label="Decrease value"
      >
        −
      </button>

      <input
        type="number"
        className="counter-value"
        value={internalValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={e => e.target.select()}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        placeholder={placeholder}
        aria-label="Counter value"
      />

      <button
        type="button"
        className="counter-btn counter-btn-plus"
        onClick={handleIncrement}
        disabled={isPlusDisabled}
        aria-label="Increase value"
      >
        +
      </button>
    </div>
  );
};

export default CounterInput;
